// Phase 2 — HTTP client for the new video draft preview-then-commit flow.
//
// All endpoints sit under /ads/videos/draft and are protected by the same
// owner-id header pattern as the rest of /ads/videos.
//
// Always wraps the underlying axios call with handleApi() per the frontend
// CLAUDE.md rule.

import { handleApi } from "@/utils/handleApi";
import { axiosInstanceWebsite } from "../../instance";
import type {
  CreateVideoDraftRequest,
  EditDraftScriptRequest,
  RegenerateDraftImageRequest,
  RegenerateDraftStyleRequest,
  VideoDraftResponse,
} from "./videoDraft.types";

const BASE = "/ads/videos/draft";

/**
 * Creates a new draft. The server validates and charges credits, then
 * dispatches the Director Creative call to conversation-engine in background.
 *
 * Returns a VideoDraftResponse with status="directing" — the frontend should
 * then poll getVideoDraft() until status="ready_for_review".
 */
export async function createVideoDraft(payload: CreateVideoDraftRequest) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(BASE, payload)
      .then((r) => r.data)
  );
}

/**
 * Polling endpoint. Returns the current state of the draft. The frontend
 * polls this every 5s while in "directing" or "media_generating" states.
 *
 * Pass `showToast=false` because polling errors should not spam the user.
 */
export async function getVideoDraft(referenceId: string) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .get<VideoDraftResponse>(`${BASE}/${referenceId}`)
      .then((r) => r.data),
    false
  );
}

/**
 * The user clicked "Aprobar y generar video". Idempotent on the server side.
 * No new credit charge — credits were already charged on createVideoDraft.
 */
export async function approveVideoDraft(referenceId: string) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/approve`)
      .then((r) => r.data)
  );
}

/**
 * The user clicked "Cancelar". Triggers a 100% refund on the server side.
 * Idempotent.
 */
export async function rejectVideoDraft(referenceId: string) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/reject`)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.4: edita el script del draft inline. No regenera imagen, no cuenta
 * como regeneración. Server valida word limits y que script_part_b contenga
 * el productName.
 */
export async function editVideoDraftScript(
  referenceId: string,
  payload: EditDraftScriptRequest
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/edit-script`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.4: regenera la imagen base.
 *
 * - Si se llama sin payload o con instruction null/vacío → regen idéntica
 *   a antes: mismo concept_visual_brief, mismo wrapper Pixar character.
 * - Si se pasa `instruction`, el backend la appendea al brief como capa
 *   extra de personalización (sin perder el wrapper). NO reemplaza nada.
 *
 * Cuenta como 1 regeneración independientemente de si hay instruction.
 */
export async function regenerateVideoDraftImage(
  referenceId: string,
  payload: RegenerateDraftImageRequest = {}
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/regenerate-image`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.4: re-corre el director Gemini con (opcionalmente) un pattern
 * forzado. Vuelve el draft a directing — el frontend debe resumir polling
 * hasta que vuelva a ready_for_review con el nuevo payload + imagen.
 */
export async function regenerateVideoDraftStyle(
  referenceId: string,
  payload: RegenerateDraftStyleRequest = {}
) {
  return handleApi<VideoDraftResponse>(
    axiosInstanceWebsite
      .post<VideoDraftResponse>(`${BASE}/${referenceId}/regenerate-style`, payload)
      .then((r) => r.data)
  );
}

/**
 * Phase 5.5: lista todos los drafts del owner que están en estados
 * intermedios (no terminales). Usado por la galería de videos para
 * mostrar "loading cards" de los drafts que están renderizando en
 * background, sobreviviendo refresh y navegación a otras partes de
 * Fluxi. Backend solo retorna los 6 estados in-flight (DIRECTING,
 * MEDIA_GENERATING, READY_FOR_REVIEW, GENERATING_IMAGE, WAITING_SCENES,
 * MERGING). Lista vacía si no hay nada en vuelo. NO toastea errores
 * porque se llama on-mount + polling continuo.
 */
export async function listInFlightVideoDrafts() {
  return handleApi<VideoDraftResponse[]>(
    axiosInstanceWebsite
      .get<VideoDraftResponse[]>(`${BASE}/in-flight`)
      .then((r) => r.data),
    false
  );
}
